import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';

// ─── DESIGN TOKENS (NOVAA dark) ──────────────────────────────────
const T = {
  bg: 'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border: 'var(--nd-w06)',
  text: 'var(--nd-text)',
  textMuted: 'var(--nd-tx35)',
};

const API = process.env.REACT_APP_API_BASE_URL;
const IMG_BASE = API ? API.replace('/api', '') : '';

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .nv-att * { box-sizing: border-box; }
  .nv-att ::-webkit-scrollbar { width: 6px; height: 6px; }
  .nv-att ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }
  .nv-att table { border-collapse: collapse; width: 100%; }
  .nv-att th { font-size: 9px; letter-spacing: 0.12em; color: var(--nd-tx35); font-weight: 700; text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--nd-w08); white-space: nowrap; }
  .nv-att td { font-size: 12px; color: var(--nd-text); padding: 10px 12px; border-bottom: 1px solid var(--nd-w05); }
  .nv-att tr:hover td { background: rgba(96,165,250,0.05); }
  .nv-att input, .nv-att select { background: var(--nd-w05); border: 1px solid var(--nd-w10); border-radius: 8px; color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px; padding: 8px 12px; outline: none; }
  .nv-att input:focus, .nv-att select:focus { border-color: rgba(96,165,250,0.5); }
  .nv-att input[type="date"] { color-scheme: dark; }
`;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const pad = (n) => String(n).padStart(2, '0');
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const daysAgoStr = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const monthStartStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; };

const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = String(s).split('-');
  if (!y || !m || !d) return s;
  return `${MONTHS[Number(m) - 1]} ${Number(d)}, ${y}`;
};
const fmtTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const AttendanceDashboard = () => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [activePreset, setActivePreset] = useState('all');
  const [foName, setFoName] = useState('');
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ kpis: {}, per_person: [], daily: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true); setError(null);
      const qs = new URLSearchParams();
      if (from) qs.append('from', from);
      if (to) qs.append('to', to);
      const q = qs.toString() ? `?${qs.toString()}` : '';
      const [recRes, sumRes] = await Promise.all([
        axios.get(`${API}/attendance/records${q}`),
        axios.get(`${API}/attendance/summary${q}`),
      ]);
      setRecords(recRes.data?.records || []);
      setSummary({
        kpis: sumRes.data?.kpis || {},
        per_person: sumRes.data?.per_person || [],
        daily: sumRes.data?.daily || [],
      });
    } catch (err) {
      setError('Failed to load attendance data');
      setRecords([]);
      setSummary({ kpis: {}, per_person: [], daily: [] });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [from, to]);

  const applyPreset = (key) => {
    setActivePreset(key);
    if (key === 'all') { setFrom(''); setTo(''); }
    else if (key === '7') { setFrom(daysAgoStr(7)); setTo(todayStr()); }
    else if (key === '30') { setFrom(daysAgoStr(30)); setTo(todayStr()); }
    else if (key === 'month') { setFrom(monthStartStr()); setTo(todayStr()); }
  };

  // CSV Download Handler
  const downloadCSV = () => {
    if (!summary.per_person || summary.per_person.length === 0) return;

    // 1. Setup headers
    const headers = ['Name', 'Days Present', 'Check-ins', 'Avg Hours', 'Missing Clock-out', 'Last Seen'];
    
    // 2. Map data into rows
    const rows = summary.per_person.map(p => {
      // Escape commas in names or dates just in case
      const name = p.fo_name ? `"${p.fo_name.replace(/"/g, '""')}"` : '—';
      const days = p.days_present || 0;
      const checkins = p.checkins || 0;
      const avgHrs = p.avg_hours != null ? p.avg_hours : '—';
      const missing = p.missing_clockout || 0;
      const lastSeen = p.last_seen ? `"${fmtDate(p.last_seen)}"` : '—';
      
      return [name, days, checkins, avgHrs, missing, lastSeen].join(',');
    });

    // 3. Combine headers and rows
    const csvContent = [headers.join(','), ...rows].join('\n');

    // 4. Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const dateRange = (from && to) ? `${from}_to_${to}` : 'All_Time';
    link.setAttribute('download', `Attendance_By_Person_${dateRange}.csv`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const q = foName.trim().toLowerCase();
  const visibleRecords = q ? records.filter(r => (r.fo_name || '').toLowerCase().includes(q)) : records;

  const k = summary.kpis || {};
  const kpiCards = [
    { label: 'CHECK-INS', value: k.total_checkins ?? 0, color: 'var(--nd-text)' },
    { label: 'PEOPLE', value: k.people ?? 0, color: '#60A5FA' },
    { label: 'ACTIVE DAYS', value: k.active_days ?? 0, color: '#A78BFA' },
    { label: 'AVG HOURS', value: (k.avg_hours != null ? k.avg_hours : '—'), color: '#22D3A5' },
    { label: 'MISSING CLOCK-OUT', value: k.missing_clockout ?? 0, color: '#F59E0B' },
  ];

  const Thumb = ({ url, tag }) => (
    url ? (
      <img
        src={`${IMG_BASE}${url}`}
        alt={tag}
        onClick={() => setLightbox(`${IMG_BASE}${url}`)}
        style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover', cursor: 'pointer', border: '1px solid var(--nd-w12)' }}
        title={`${tag} photo — click to enlarge`}
      />
    ) : (
      <span style={{ fontSize: 9, color: 'var(--nd-w20)' }}>{tag}: —</span>
    )
  );

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' };

  return (
    <div className="nv-att" style={{ background: T.bg, fontFamily: "'DM Sans', sans-serif", color: T.text, padding: '14px 18px', minHeight: '100vh' }}>
      <style>{GLOBAL}</style>

      {/* Header */}
      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.18em', marginBottom: 5 }}>NOVAA · C&I MANAGEMENT</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>Attendance</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 5 }}>Field-ops check-ins, hours & history</div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />

        {/* Controls */}
        <div style={{ padding: '12px 22px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4, background: 'var(--nd-w04)', padding: 3, borderRadius: 10, border: `1px solid ${T.border}` }}>
            {[['all', 'All time'], ['7', 'Last 7d'], ['30', 'Last 30d'], ['month', 'This month']].map(([key, label]) => (
              <button key={key} onClick={() => applyPreset(key)}
                style={{ padding: '7px 14px', background: activePreset === key ? 'linear-gradient(135deg,#3B82F6,#22D3A5)' : 'transparent', color: activePreset === key ? 'var(--nd-text-strong)' : T.textMuted, border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setActivePreset(''); }} />
            <span style={{ color: T.textMuted, fontSize: 12 }}>→</span>
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setActivePreset(''); }} />
          </div>
          <input placeholder="Search name…" value={foName} onChange={e => setFoName(e.target.value)} style={{ minWidth: 160 }} />
          <button onClick={fetchData} style={{ marginLeft: 'auto', padding: '8px 16px', background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>↻ Refresh</button>
        </div>
      </div>

      {error && <div style={{ ...card, padding: '14px 20px', marginBottom: 14, color: '#F87171', fontSize: 13 }}>⚠ {error}</div>}

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 14 }}>
        {kpiCards.map(({ label, value, color }) => (
          <div key={label} style={{ ...card, padding: '16px 18px' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{loading ? '—' : value}</div>
            <div style={{ fontSize: 8, color: T.textMuted, letterSpacing: '0.14em', marginTop: 6, fontWeight: 600 }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14, marginBottom: 14, alignItems: 'start' }}>
        {/* Daily trend */}
        <div style={{ ...card, padding: '16px 18px' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.textMuted, letterSpacing: '0.15em', marginBottom: 14 }}>DAILY CHECK-INS</div>
          {summary.daily.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No data for this range</div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={summary.daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--nd-w06)" />
                <XAxis dataKey="date" tick={{ fill: 'var(--nd-tx40)', fontSize: 10 }} tickFormatter={(d) => { const p = String(d).split('-'); return p.length === 3 ? `${MONTHS[Number(p[1]) - 1]} ${Number(p[2])}` : d; }} />
                <YAxis allowDecimals={false} tick={{ fill: 'var(--nd-tx40)', fontSize: 10 }} />
                <Tooltip contentStyle={{ background: 'var(--nd-surface-2)', border: '1px solid var(--nd-w10)', borderRadius: 8, color: 'var(--nd-text)', fontSize: 12 }} labelFormatter={fmtDate} cursor={{ fill: 'rgba(96,165,250,0.08)' }} />
                <Bar dataKey="checkins" name="Check-ins" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="people" name="People" fill="#22D3A5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Per-person */}
        <div style={{ ...card }}>
          {/* UPDATED HEADER: Flexbox for alignment and CSV Button added */}
          <div style={{ padding: '12px 18px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.textMuted, letterSpacing: '0.15em' }}>BY PERSON</div>
            <button 
              onClick={downloadCSV} 
              disabled={summary.per_person.length === 0}
              style={{ 
                padding: '6px 12px', 
                background: 'var(--nd-w05)', 
                border: `1px solid ${T.border}`, 
                borderRadius: 6, 
                color: summary.per_person.length === 0 ? 'var(--nd-w20)' : T.text, 
                cursor: summary.per_person.length === 0 ? 'not-allowed' : 'pointer', 
                fontSize: 11, 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              📥 Download CSV
            </button>
          </div>
          <div style={{ maxHeight: 250, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Days</th><th>Check-ins</th><th>Avg hrs</th><th>No out</th><th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {summary.per_person.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: T.textMuted, padding: '24px' }}>No data</td></tr>
                ) : summary.per_person.map((p) => (
                  <tr key={p.fo_name}>
                    <td style={{ fontWeight: 700 }}>{p.fo_name}</td>
                    <td>{p.days_present}</td>
                    <td>{p.checkins}</td>
                    <td>{p.avg_hours != null ? p.avg_hours : '—'}</td>
                    <td style={{ color: p.missing_clockout > 0 ? '#F59E0B' : T.textMuted }}>{p.missing_clockout}</td>
                    <td style={{ color: T.textMuted }}>{fmtDate(p.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail table */}
      <div style={{ ...card }}>
        <div style={{ padding: '16px 18px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.textMuted, letterSpacing: '0.15em' }}>ATTENDANCE RECORDS</div>
          <div style={{ fontSize: 10, color: T.textMuted, fontFamily: "'DM Mono', monospace" }}>{visibleRecords.length} shown</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Date</th><th>In</th><th>Out</th><th>Hours</th><th>Client</th><th>Site</th><th>Shift</th><th>Status</th><th>Photos</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', color: T.textMuted, padding: '32px' }}>Loading…</td></tr>
              ) : visibleRecords.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', color: T.textMuted, padding: '32px' }}>No attendance records</td></tr>
              ) : visibleRecords.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 700 }}>{r.fo_name}</td>
                  <td style={{ color: T.textMuted }}>{fmtDate(r.attendance_date)}</td>
                  <td>{fmtTime(r.clock_in_at)}</td>
                  <td style={{ color: r.clock_out_at ? T.text : '#F59E0B' }}>{r.clock_out_at ? fmtTime(r.clock_out_at) : 'no out'}</td>
                  <td style={{ fontFamily: "'DM Mono', monospace" }}>{r.hours_worked != null ? r.hours_worked : '—'}</td>
                  <td>{r.client_name || '—'}</td>
                  <td style={{ color: T.textMuted }}>{r.site_name || '—'}</td>
                  <td style={{ color: T.textMuted, textTransform: 'capitalize' }}>{r.shift || '—'}</td>
                  <td>
                    <span style={{ fontSize: 9, fontWeight: 800, color: '#22D3A5', background: 'rgba(34,211,165,0.12)', padding: '2px 8px', borderRadius: 5 }}>{(r.status || '').toUpperCase()}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <Thumb url={r.clock_in_image_url} tag="IN" />
                      <Thumb url={r.clock_out_image_url} tag="OUT" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 24 }}>
          <img src={lightbox} alt="Attendance" style={{ maxWidth: '90vw', maxHeight: '88vh', borderRadius: 12, border: '1px solid var(--nd-w15)' }} />
        </div>
      )}
    </div>
  );
};

export default AttendanceDashboard;